/** SUNTRONIX ONE — Catalogue Cleanup AI (Category / Brand enrichment).
 *  ONE-AI suggests classifications for missing/mis-classified products;
 *  ops review and bulk-approve. Suggestions never auto-overwrite verified data.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Sparkles, CheckCheck, Loader2, AlertTriangle } from "lucide-react";

export default function CatalogueCleanup() {
  const [flags, setFlags] = useState({});
  const [rows, setRows] = useState([]);
  const [masters, setMasters] = useState({ categories: [], brands: [] });
  const [filter, setFilter] = useState("both");
  const [sel, setSel] = useState({});      // product_id -> {category, brand}
  const [loading, setLoading] = useState(true);
  const [suggesting, setSuggesting] = useState(false);
  const [approving, setApproving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/catalogue-ai/flags"),
      api.get("/catalogue-ai/queue", { params: { filter, limit: 50 } }),
    ]).then(([f, q]) => { setFlags(f.data); setRows(q.data.items || []); })
      .catch(() => toast.error("Load failed")).finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => {
    Promise.all([
      api.get("/catalogue-masters/categories"),
      api.get("/catalogue-masters/brands"),
    ]).then(([c, b]) => setMasters({
      categories: (c.data.items || []).filter((x) => x.active).map((x) => x.name),
      brands: (b.data.items || []).filter((x) => x.active).map((x) => x.name),
    })).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const runSuggest = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.post("/catalogue-ai/suggest", { limit: 20 });
      toast[data.ai_available ? "success" : "warning"](
        data.ai_available ? `ONE-AI suggested ${data.suggested} product(s)` : "AI unavailable — try again");
      // preselect suggestions into the editor
      const next = { ...sel };
      (data.items || []).forEach((it) => {
        next[it.id] = { category: it.ai_category_suggestion || "", brand: it.ai_brand_suggestion || "" };
      });
      setSel(next); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Suggest failed"); }
    finally { setSuggesting(false); }
  };

  const setField = (id, k, v) => setSel((s) => ({ ...s, [id]: { ...s[id], [k]: v } }));

  const approve = async () => {
    const items = Object.entries(sel)
      .map(([product_id, v]) => ({ product_id, category: v.category || undefined, brand: v.brand || undefined }))
      .filter((x) => x.category || x.brand);
    if (items.length === 0) { toast.error("Nothing selected to approve"); return; }
    setApproving(true);
    try {
      const { data } = await api.post("/catalogue-ai/approve", { items });
      toast.success(`Approved ${data.approved} product(s)`); setSel({}); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
    finally { setApproving(false); }
  };

  const FLAGS = [
    ["missing_category", "Missing category", "text-amber-600"],
    ["missing_brand", "Missing brand", "text-amber-600"],
    ["unknown_brand", "Unknown brand", "text-rose-600"],
    ["total_products", "Total products", "text-slate-700"],
  ];

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-5" data-testid="catalogue-cleanup-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-indigo-600 grid place-items-center text-white"><Sparkles className="h-5 w-5" /></div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Catalogue Cleanup AI</h1>
          <p className="text-[13px] text-slate-500">ONE-AI suggests Category &amp; Brand for products that need it — you review and approve.</p>
        </div>
        <Button onClick={runSuggest} disabled={suggesting} data-testid="cc-suggest" className="bg-indigo-600 hover:bg-indigo-700">
          {suggesting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}Suggest with AI
        </Button>
        <Button onClick={approve} disabled={approving} data-testid="cc-approve" variant="outline">
          {approving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCheck className="h-4 w-4 mr-1" />}Approve selected
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cc-flags">
        {FLAGS.map(([k, label, color]) => (
          <div key={k} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`cc-flag-${k}`}>
            <div className="text-[10px] font-medium text-slate-500 flex items-center gap-1">
              {k === "unknown_brand" && <AlertTriangle className="h-3 w-3 text-rose-500" />}{label}
            </div>
            <div className={"text-[22px] font-bold tabular-nums " + color}>{flags[k] ?? 0}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2">
        {[["both", "Missing either"], ["missing_category", "Missing category"], ["missing_brand", "Missing brand"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`cc-filter-${v}`}
            className={"text-[11px] px-3 py-1 rounded-full border " + (filter === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300")}>{l}</button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-[12.5px]" data-testid="cc-table">
          <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
            {["Product", "Current", "AI suggestion", "Category", "Brand"].map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline" /></td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">Nothing needs cleanup in this view.</td></tr>}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`cc-row-${r.id}`}>
                <td className="px-3 py-2 text-slate-800 max-w-xs truncate">{r.product_name}<div className="text-[10px] text-slate-400 font-mono">{r.sku || "—"}</div></td>
                <td className="px-3 py-2 text-[11px] text-slate-500">{r.category || "—"} / {r.brand || "—"}</td>
                <td className="px-3 py-2 text-[11px]">
                  {(r.ai_category_suggestion || r.ai_brand_suggestion) ? (
                    <span className="text-indigo-600">{r.ai_category_suggestion || "?"} / {r.ai_brand_suggestion || "?"}
                      {r.ai_confidence != null && <span className="text-slate-400"> · {Math.round(r.ai_confidence * 100)}%</span>}</span>
                  ) : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-3 py-2">
                  <select value={sel[r.id]?.category ?? (r.ai_category_suggestion || "")} onChange={(e) => setField(r.id, "category", e.target.value)}
                    data-testid={`cc-cat-${r.id}`} className="h-8 rounded-md border border-slate-200 px-1.5 text-[12px] w-40">
                    <option value="">—</option>
                    {masters.categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2">
                  <select value={sel[r.id]?.brand ?? (r.ai_brand_suggestion || "")} onChange={(e) => setField(r.id, "brand", e.target.value)}
                    data-testid={`cc-brand-${r.id}`} className="h-8 rounded-md border border-slate-200 px-1.5 text-[12px] w-36">
                    <option value="">—</option>
                    <option value="Unbranded">Unbranded</option>
                    {masters.brands.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
